import SectionWrapper from './SectionWrapper';
import { Flex, Link, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { useHighlightSection } from '../../../hooks/useHighlightSection';
import { setLocationProp } from '../../../store/redux/slices/template.slice';
import type { LocationInput } from '../../../store/types/template';
import { locationHasPlacedPin } from './utils/locationUtils';
import LocationWrapper from './LocationWrapper';
import AddMoreButton from './AddMoreButton';
import SearchableMap from './SearchableMap';
import { CustomInfoIcon } from '../../../theme/icons/CustomInfoIcon';
import {
  BUILDER_DEVICE_TAB,
  type BuilderDeviceTab,
} from './utils/templateBuilderTabs';

const MAX_LOCATIONS = 10;

const GEOFENCING_GUIDE_URL =
  'https://docs.passentry.com/documentation/passes/geofencing/guide';

const GOOGLE_WALLET_BEHAVIOUR_ITEMS = [
  'Pass appears automatically when customers are nearby',
  'Trigger distance determined by Google (~50m-1km)',
  'Custom notifications are not supported',
] as const;

const APPLE_WALLET_BEHAVIOUR_ITEMS = [
  'Custom notification messages supported',
  'One message per location',
  'If locations overlap, Apple chooses which notification appears',
] as const;

function WalletBehaviourSection({
  title,
  items,
}: {
  title: string;
  items: readonly string[];
}) {
  return (
    <Flex
      direction="column"
      borderRadius="5px"
      border="1px solid"
      borderColor="lightGrey"
      p={3}
    >
      <Text textStyle="bodyFamilyMedium" fontSize="14px" pl={1}>
        {title}
      </Text>
      <UnorderedList color="#666666" spacing="-3px">
        {items.map((text) => (
          <ListItem key={text}>
            <Text
              textStyle="bodyFamilyRegular"
              fontSize="12px"
              lineHeight="short"
            >
              {text}
            </Text>
          </ListItem>
        ))}
      </UnorderedList>
    </Flex>
  );
}

export type LocationsFeatureProps = {
  selectedTab: BuilderDeviceTab;
  onNavigateToSharedGeofencing: () => void;
  onNavigateToAppleLocationNotifications: (locationId: string) => void;
  pendingExpandLocationIndex?: number | null;
  onPendingExpandLocationConsumed?: () => void;
};

const locationRowDomId = (locationId: string) =>
  `location-row-${locationId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const LocationsFeature = ({
  selectedTab,
  onNavigateToSharedGeofencing,
  onNavigateToAppleLocationNotifications,
  pendingExpandLocationIndex,
  onPendingExpandLocationConsumed,
}: LocationsFeatureProps) => {
  const isAppleTab = selectedTab === BUILDER_DEVICE_TAB.APPLE;
  const isGoogleTab = selectedTab === BUILDER_DEVICE_TAB.GOOGLE;
  const isSharedTab = selectedTab === BUILDER_DEVICE_TAB.SHARED;

  useHighlightSection({
    matchValue: 'locations',
    getElement: () => document.getElementById('locations-section'),
  });
  const locations = useAppSelector((s) => s.template.locations);
  const dispatch = useAppDispatch();
  const [expandedLocationId, setExpandedLocationId] = useState<string | null>(
    null
  );
  const [mapEditIndex, setMapEditIndex] = useState(0);
  const [mapSearchResetKey, setMapSearchResetKey] = useState(0);
  const [placementHintVisible, setPlacementHintVisible] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState(false);

  const hasPlacedLocations = useMemo(
    () => locations.some((l) => locationHasPlacedPin(l)),
    [locations]
  );

  const autoOpenSectionSignal = locations.length > 0;

  useEffect(() => {
    if (locations.length === 0 && !pendingPlacement) {
      setPlacementHintVisible(false);
    }
  }, [locations.length, pendingPlacement]);

  useEffect(() => {
    if (!isSharedTab) setPendingPlacement(false);
  }, [isSharedTab]);

  useEffect(() => {
    if (!pendingPlacement) return;
    if (expandedLocationId == null) return;
    setPendingPlacement(false);
  }, [expandedLocationId, pendingPlacement]);

  useEffect(() => {
    if (!expandedLocationId) return;
    const i = locations.findIndex((l) => l._id === expandedLocationId);
    if (i >= 0) setMapEditIndex(i);
  }, [expandedLocationId, locations]);

  const safeMapEditIndex =
    locations.length === 0 ? 0 : Math.min(mapEditIndex, locations.length - 1);

  const locationIndexForMap =
    isSharedTab && pendingPlacement
      ? locations.length
      : locations.length === 0
        ? 0
        : safeMapEditIndex;

  const handlePendingPlacementComplete = useCallback(() => {
    setPendingPlacement(false);
  }, []);

  const handleNavigateToAppleLocationNotifications = useCallback(
    (locationId: string) => {
      setExpandedLocationId(locationId);
      onNavigateToAppleLocationNotifications(locationId);
    },
    [onNavigateToAppleLocationNotifications]
  );

  useEffect(() => {
    for (let index = 0; index < locations.length; index++) {
      const loc = locations[index];
      if (!locationHasPlacedPin(loc) || !loc._newlyAdded) continue;
      setExpandedLocationId(loc._id);
      dispatch(
        setLocationProp({
          index,
          field: '_newlyAdded' as keyof LocationInput,
          value: false,
        })
      );
      break;
    }
  }, [locations, dispatch]);

  useEffect(() => {
    if (
      expandedLocationId &&
      !locations.some((l) => l._id === expandedLocationId)
    ) {
      setExpandedLocationId(null);
    }
  }, [locations, expandedLocationId]);

  useEffect(() => {
    if (pendingExpandLocationIndex == null) return;
    if (!onPendingExpandLocationConsumed) return;

    const loc = locations[pendingExpandLocationIndex];
    if (!loc?._id) {
      if (pendingExpandLocationIndex >= locations.length) {
        onPendingExpandLocationConsumed();
      }
      return;
    }

    setExpandedLocationId(loc._id);
    window.setTimeout(() => {
      document
        .getElementById(locationRowDomId(loc._id))
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 280);
    onPendingExpandLocationConsumed();
  }, [pendingExpandLocationIndex, locations, onPendingExpandLocationConsumed]);

  const handleAddLocation = useCallback(() => {
    const newRowIndex = locations.length;
    setExpandedLocationId(null);
    setMapEditIndex(newRowIndex);
    setMapSearchResetKey((k) => k + 1);
    setPendingPlacement(true);
  }, [locations.length]);

  const limitReached = locations.length >= MAX_LOCATIONS;

  const sectionSubtitle = isAppleTab ? (
    <Text>
      Add a custom message that appears when a customer is near one of your
      locations.
    </Text>
  ) : isGoogleTab ? (
    <Text>
      Google controls the trigger radius (~50m-1km) and notification
      automatically.
      <Link
        color="linkBlue"
        href="https://docs.passentry.com/documentation/passes/geofencing/guide#google-wallet-geofence-radius"
        target="_blank"
        ml={1}
      >
        Learn more
      </Link>
    </Text>
  ) : (
    <Text>
      Add locations so your pass appears when customers are nearby. Read our{' '}
      <Link
        color="linkBlue"
        href={GEOFENCING_GUIDE_URL}
        target="_blank"
      >
        guide
      </Link>
      .
    </Text>
  );

  return (
    <SectionWrapper
      id="locations-section"
      selectedTab={selectedTab}
      autoOpenOnceWhen={autoOpenSectionSignal}
      bg={
        placementHintVisible && (locations.length > 0 || pendingPlacement)
          ? '#F0F0F0'
          : 'white'
      }
      title={
        isAppleTab || isGoogleTab ? 'Geofencing notifications' : 'Geofencing'
      }
      tooltip={
        isSharedTab
          ? 'Geofencing controls when a pass appears near a location. Apple Wallet uses a radius you control (10-100m). Google Wallet determines the trigger distance automatically and it cannot be customised (~50m-1km).'
          : isAppleTab
            ? 'Apple Wallet can show a custom message when a customer is near one of your locations. Messages are optional and can be set per location.'
            : 'Google Wallet shows the pass on the lock screen, when a customer is near one of your locations.'
      }
      subtitle={sectionSubtitle}
    >
      <Flex direction="column" gap={4}>
        {isGoogleTab && (
          <WalletBehaviourSection
            title="Google Wallet behaviour"
            items={GOOGLE_WALLET_BEHAVIOUR_ITEMS}
          />
        )}
        {isAppleTab && (
          <WalletBehaviourSection
            title="Apple Wallet behaviour"
            items={APPLE_WALLET_BEHAVIOUR_ITEMS}
          />
        )}
        {!isSharedTab && (
          <Flex
            width="100%"
            borderRadius="5px"
            borderColor="lightGrey"
            bg="#E5F6FA"
            px={3}
            py={2}
            alignItems="center"
            gap={3}
          >
            <CustomInfoIcon boxSize={4} color="#009BD6" />
            <Text textStyle="bodyFamilyRegular" fontSize="14px">
              Locations are managed in{' '}
              <Link
                as="button"
                type="button"
                variant="unstyled"
                display="inline"
                textDecoration="underline"
                textUnderlineOffset={2}
                cursor="pointer"
                fontSize="inherit"
                fontFamily="inherit"
                lineHeight="inherit"
                verticalAlign="baseline"
                onClick={onNavigateToSharedGeofencing}
              >
                shared features
              </Link>
            </Text>
          </Flex>
        )}
        {isSharedTab || locations.length > 0 ? (
          <SearchableMap
            locationIndex={locationIndexForMap}
            searchResetKey={mapSearchResetKey}
            expandedLocationId={expandedLocationId}
            onSelectLocationById={setExpandedLocationId}
            onPlacementHintVisibleChange={setPlacementHintVisible}
            previewOnly={!isSharedTab}
            pendingPlacement={isSharedTab && pendingPlacement}
            onPendingPlacementComplete={handlePendingPlacementComplete}
          />
        ) : null}
        {isSharedTab || hasPlacedLocations ? (
          <Flex direction="row" alignItems="baseline" gap={1}>
            <Text textStyle="bodyFamilySemiBold" fontSize="16px">
              Locations
            </Text>
            {isSharedTab && (
              <Text textStyle="bodyFamilyRegular" fontSize="14px">
                ({locations.length}/{MAX_LOCATIONS})
              </Text>
            )}
          </Flex>
        ) : null}
        {hasPlacedLocations ? (
          <Flex direction="column" gap={4}>
            {locations.map((location, index) =>
              locationHasPlacedPin(location) ? (
                <LocationWrapper
                  key={location._id}
                  location={location}
                  locationId={location._id}
                  index={index}
                  isExpanded={expandedLocationId === location._id}
                  onToggle={() =>
                    setExpandedLocationId((cur) =>
                      cur === location._id ? null : location._id
                    )
                  }
                  onRequireOpen={setExpandedLocationId}
                  newlyAdded={location._newlyAdded}
                  appleNotificationsOnly={isAppleTab}
                  googleWalletNoticeOnly={isGoogleTab}
                  mutedLocationHeader={!isSharedTab}
                  onNavigateToAppleNotification={
                    isSharedTab
                      ? handleNavigateToAppleLocationNotifications
                      : undefined
                  }
                />
              ) : null
            )}
          </Flex>
        ) : null}
        {isSharedTab && limitReached && locations.length > 0 ? (
          <Text
            fontSize="14px"
            textStyle="bodyFamilyRegular"
            textAlign="center"
          >
            Maximum of {MAX_LOCATIONS} locations reached
          </Text>
        ) : null}
        {isSharedTab && (
          <AddMoreButton
            text="Add location"
            onClick={handleAddLocation}
            isDisabled={limitReached || pendingPlacement}
          />
        )}
      </Flex>
    </SectionWrapper>
  );
};

export default LocationsFeature;
